import useGetUsers from "@/hooks/useGetUsers";
import { Box, Typography, Skeleton, Paper, Alert } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import UserTree from "./myUserTree";
import { useSession } from "@/hooks/useSession";
import { useTranslation } from "react-i18next";
import GroupsIcon from "@mui/icons-material/Groups";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const MyTeam = () => {
  const { t } = useTranslation();
  const { users, errorUser, usersLoading } = useGetUsers();
  const { session } = useSession();
  const theme = useTheme();

  if (usersLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 4,
        }}
      >
        {/* Header Skeleton */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={200} height={32} />
          </Box>
          <Skeleton variant="text" width={300} height={20} />
        </Paper>

        {/* Table Skeleton */}
        <Paper
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3 }}>
            {[...Array(5)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  py: 2,
                  borderBottom: index < 4 ? "1px solid" : "none",
                  borderColor: "divider",
                }}
              >
                <Skeleton variant="rectangular" width={24} height={24} />
                <Skeleton variant="text" width="25%" height={24} />
                <Skeleton variant="text" width="25%" height={24} />
                <Skeleton variant="text" width="25%" height={24} />
                <Skeleton variant="circular" width={32} height={32} />
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  if (errorUser || !users) {
    return (
      <Box
        sx={{
          width: "100%",
          p: 4,
        }}
      >
        {/* Header */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <GroupsIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {t("myTeam.title")}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {t("myTeam.subtitle")}
          </Typography>
        </Paper>

        {/* Error State */}
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.error.main, 0.2),
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {t("myTeam.errorText")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        p: 4,
      }}
      aria-label={t("myTeam.teamAria")}
    >
      {/* Header */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: theme.shadows[2],
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <GroupsIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            {t("myTeam.title")}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("myTeam.subtitle")}
        </Typography>
      </Paper>

      {/* Team Tree */}
      <UserTree users={users} currentUserId={session.id} />
    </Box>
  );
};

export default MyTeam;
